import $ from 'jquery'
import _ from 'lodash'
import Backbone from 'backbone'
import template from './wizard-progress.hbs'
import icon from 'scoreboard/components/icon'

var animationEnd = 'webkitAnimationEnd mozAnimationEnd oAnimationEnd oanimationend animationend MSAnimationEnd'

// Width of the step (stem + bubble)
var STEP_WIDTH = 42
var STEM_WIDTH = 25

/**
 * The step portion of the wizard progress indicator which is made up of a
 * stem and a bubble portion.
 */
var WizardProgressStep = Backbone.View.extend({

	tagName: 'li',

	initialize: function(options) {
		this.options = options
	},

	render: function() {
		// Add the data-step attribute
		this.$el.attr('data-step', this.options.step)

		// Add the correct classes to the node
		var classList = (this.options.content ? 'wizard-progress-bubble ': 'wizard-progress-stem ') +
						(this.options.isFilled ? 'is-filled ' : '') +
						(this.options.isNavigable ? 'is-navigable' : '')

		this.$el.addClass(classList)

		// Add content to bubble nodes
		this.$el.append('<span>' + this.options.content + '</span>')

		// If we're adding this step w/o animations, set the opacity immediately,
		// else wait till after the animation to set it (keep the step visible)
		if (this.options.disableAnimations) {
			this.$el.css('opacity', 1)
		} else {
			this.$el.on(animationEnd, function() {
				this.$el.css('opacity', 1)
			}.bind(this))
		}

		return this
	}
})

/**
 * A progress indicator shown at the bottom of wizards.  Also allows clicking
 * to jump non-linearly between the steps of the wizard (e.g. jump from step 3
 * back to step 1 and then back to step 3).
 */
var WizardProgress = Backbone.View.extend({

	className: 'wizard-progress',

	events: {
		'click .is-navigable[data-step]': '_jump'
	},

	/**
	 * options.step is the number of the current step
	 * options.steps is the number of steps
	 * options.lastNavigable is the last navigable step
	 */
	initialize: function(options) {
		this.options = options || {}
	},

	render: function() {
		var status = this._status()
		this.$el.html(template(this.options))

		// Define these vars explicitly so they can be easily retrieved from
		// outside the wizard
		this.step = this.options.step
		this.steps = this.options.steps
		this.lastNavigable = this.options.lastNavigable
		this.$el.addClass('is-rendering')

		// We have two initial rendering options. This is determined by this.options.openingAnimation
		// 1. (true) Render one step at a time sequentially which gives a nice animated feel
		// 2. (false) Render all steps at once, no animation/movement.
		this.toggleAnimation(this.options.openingAnimation)

		// Go through and add steps for as many steps as we were passed in the options
		for (var step=1; step<=this.steps; step++) {
			this.addStep({
				isNavigable: step <= this.lastNavigable,
				isFilled: step <= this.step,
				step: step,
				disableAnimations: status.animationDisabled
			})
		}

		this.toggleAnimation(true)
		this.$el.removeClass('is-rendering')
		return this
	},

	// Animate the control forward one step
	forward: function() {
		return this._step('forward')
	},

	// Animate the control backward one step
	back: function() {
		return this._step()
	},

	// Adds a new step to the wizard progress control
	addStep: function (opts) {
		var deferred = new $.Deferred()
		var status = this._status()

		// Don't add any new steps under these conditions
		if (status.isAnimating || status.isOpening) {
			deferred.resolve()
			return deferred
		}

		this.$el.addClass('is-adding-step')

		var wizardStem
		if (opts) {
			wizardStem = new WizardProgressStep(opts).render()
		} else {
			// If there were no opts given, that means either this was called directly to
			// create a new step at the end of the control or it was called while
			// we're extending the wizard in setStep()
			this.steps++

			opts = {
				step: this.steps,
				isFilled: false,
				isNavigable: _.isFinite(this.lastNavigable) ? this.lastNavigable >= this.steps : true
			}

			if (opts.isNavigable) {
				this.lastNavigable = this.steps
			}

			// Create the stem here before adding 'content' which separates stem from bubble
			wizardStem = new WizardProgressStep(opts).render()
			opts.content = this.steps
		}

		// If we're not showing animations at the moment, be sure to pass that info
		// along to the step for when it's created
		_.extend(opts, {
			disableAnimations: status.animationDisabled
		})

		var wizardStep = new WizardProgressStep(_.extend(opts, {
			content: opts.step < this.step ? icon('approved') : opts.step
		})).render()

		// If we don't already have enough space for the new step, make room
		var currentWidth = this.$el.width()
		if (currentWidth <= (this.steps * STEP_WIDTH) - STEM_WIDTH) {
			this.$el.width(currentWidth + STEP_WIDTH)
		}

		this.$('.wizard-progress-indicator').append(wizardStem.el, wizardStep.el)

		// Animate the first step's arrival
		var step = this._getStep(this.steps)
		step.addClass('is-adding')

		// If we're doing the initial render, don't append 'is-animating'
		// because it doesn't apply in that case, and it'll disrupt the opening.
		if (!status.isRendering) {
			// Make sure animations aren't disabled
			if (!status.animationDisabled) {
				this.$el.addClass('is-animating')
			}
		}

		var completeAddStep = function(step) {
			step.removeClass('is-adding')
			this.$el.removeClass('is-animating')
			this.$el.removeClass('is-adding-step')
			// If a width was assigned to this.$el, nix it here
			this.$el.removeAttr('style')
			deferred.resolve()
		}.bind(this)

		// Nix animation classes right away if there's no animating at the moment,
		// else, wait till the animations are over before we nix them.
		if (status.animationDisabled) {
			completeAddStep(step)
		} else {
			step.on(animationEnd, function() {
				completeAddStep(step)
			})
		}

		return deferred
	},

	// Remove a step from the end of the wizard progress control
	removeStep: function () {
		var deferred = new $.Deferred()
		var status = this._status()

		// Don't remove any steps under these conditions
		if (this.steps == 1 || status.isAnimating || status.isOpening) {
			deferred.resolve()
			return deferred
		}

		// If we're currently at the last step, step backwards before removing it
		if (this.step == this.steps) {
			this.back()
		}

		var completeRemoveStep = function() {
			step.remove()
			this.steps--
			this.$el.removeClass('is-animating')
			deferred.resolve()
		}.bind(this)

		// Add the animation classes and remove the step when it's done
		var step = this._getStep(this.steps)
		if (status.animationDisabled) {
			completeRemoveStep()
		} else {
			step.addClass('is-removing')
			this.$el.addClass('is-animating')
			step.on(animationEnd, completeRemoveStep)
		}

		return deferred
	},

	// Sets the steps/step/lastNavigable values for the wizard progress and
	// kicks off the related animating functions in turn.
	setValues: function(values) {
		if (values) {
			// Don't attempt to set new values under these conditions
			if (this.lastNavigable == values.lastNavigable
				&& this.step == values.step
				&& this.steps == values.steps) {
				return
			}

			this.lastNavigable = values.lastNavigable || this.lastNavigable
			// First, set the steps, then after that's done, go to the given step.
			// This is done sequentially so that the visual animation is smooth
			// and also so that internal values don't get mixed up trying to
			// perform two different animations at once.
			this._setSteps(values.steps).then(function() {
				this._goToStep(values.step)
			}.bind(this))
		}
	},

	// Handles disabling/enabling wizard progress animations/transitions.
	toggleAnimation: function(toggle) {
		this.$el.toggleClass('disable-animations', !toggle)
	},

	// Iterate up or down to the desired step
	_goToStep: function(step) {
		var deferred = new $.Deferred()
		var status = this._status()

		// Don't recurse to any steps under these conditions
		if (!step || status.isOpening || status.isAnimating) {
			deferred.resolve()
			return deferred
		}

		// If we're traveling more than just one step in either direction, turn
		// animations off and just go there.
		if (Math.abs(step - this.step) > 1) {
			this.toggleAnimation(false)
		}

		this.$el.addClass('is-going-to-step')
		// Step up to the desired step
		if (step > this.step) {
			var stepForward = function() {
				if (step > this.step) {
					this.forward().then(function() {
						stepForward()
					})
				} else {
					this.$el.removeClass('is-going-to-step')
					this.toggleAnimation(true)
					deferred.resolve()
				}
			}.bind(this)
			stepForward()
		// Step down to the desired step
		} else if (step < this.step) {
			var stepBackward = function() {
				if (step < this.step) {
					this.back().then(function() {
						stepBackward()
					})
				} else {
					this.$el.removeClass('is-going-to-step')
					this.toggleAnimation(true)
					deferred.resolve()
				}
			}.bind(this)
			stepBackward()
		} else {
			this.toggleAnimation(true)
		}

		return deferred
	},

	// Create or delete steps until we end up with the desired amount of steps passed in.
	// We're using promises in order to recursively add/remove steps using this.addStep
	// and this.removeStep() so that the manipulation goes smoothly
	_setSteps: function(steps) {
		var deferred = new $.Deferred()
		var status = this._status()

		// Don't try to change the number of steps in the control under these conditions
		if (this.settingSteps || !steps || status.isOpening) {
			deferred.resolve()
			return deferred
		}

		this.$el.addClass('is-setting-steps')

		// We're under, add steps as needed
		if (steps > this.steps) {
			var addSteps = function() {
				if (steps > this.steps) {
					this.addStep().then(function() {
						addSteps()
					})
				} else {
					this.$el.removeClass('is-setting-steps')
					deferred.resolve()
				}
			}.bind(this)
			addSteps()
		// We're over, take away steps as needed
		} else if (steps < this.steps) {
			var removeSteps = function() {
				if (steps < this.steps) {
					this.removeStep().then(function() {
						removeSteps()
					})
				} else {
					this.$el.removeClass('is-setting-steps')
					deferred.resolve()
				}
			}.bind(this)
			removeSteps()
		// We're at the step we need to be, resolve the promise immediately
		} else {
			this.$el.removeClass('is-setting-steps')
			deferred.resolve()
		}
		return deferred
	},

	// Performs a step in a given direction by a one step increment
	_step: function(direction) {
		var deferred = new $.Deferred()
		var status = this._status()
		var isForward = direction == 'forward'

		// Don't allow any stepping under these conditions
		if (status.isOpening || isForward && this.step == this.steps
			|| !isForward && this.step == 1) {
			deferred.resolve()
			return deferred
		}

		this.$el.addClass('is-stepping')
		// If step animations are allowed, turn them on using .is-animating
		this.$el.toggleClass('is-animating', !status.animationDisabled)

		// Get current and next step elements
		var currentStep = this._getStep(this.step)
		var nextStep = this._getStep(isForward ? this.step + 1 : this.step - 1)

		// Remove old classes since the current step isn't selected anymore
		currentStep.removeClass('is-selected')

		// If moving forward, mark the next step as filled and place a checkmark
		// on the current step
		if (isForward) {
			nextStep.addClass('is-filled')
			currentStep.find('span').html(icon('approved'))
		// If moving backward, remove .is-filled and replace the check icon with
		// the step number on both current and next steps
		} else {
			currentStep.removeClass('is-filled')
				.find('span').html(this.step)
			nextStep.find('span').html(this.step - 1)
		}

		if (!status.animationDisabled) {
			nextStep.eq(1).one(animationEnd, function() {
				this.$el.removeClass('is-animating')
				nextStep.removeClass('is-selected')
				deferred.resolve()
			}.bind(this))
		}

		nextStep.addClass('is-selected')

		// Reset this.step now that we've finished stepping
		if (isForward) {
			this.step++
		} else {
			this.step--
		}

		this.$el.removeClass('is-stepping')

		// If animations are disabled, immediately remove .is-selected and
		// resolve the promise, else wait till the animation is done to do that
		if (status.animationDisabled) {
			nextStep.removeClass('is-selected')
			deferred.resolve()
		}

		return deferred
	},

	// Returns the correct step (stem+bubble) for a given step number
	_getStep: function(step) {
		return this.$('li[data-step=' + step + ']')
	},

	// triggers a 'jump' event when the user clicks on a step to jump to that step.
	_jump: function(e) {
		var step = this.$(e.target).closest('li').data('step')
		if (step != this.step) {
			this.trigger('jump', step)
		}
	},

	// Returns an object which gives a glimpse into what the wizard is doing at any
	// given moment
	_status: function() {
		return {
			animationDisabled: this.$el.hasClass('disable-animations'),
			isOpening: this.$el.hasClass('is-opening'),
			isAnimating: this.$el.hasClass('is-animating'),
			isSettingSteps: this.$el.hasClass('is-setting-steps'),
			addingStep: this.$el.hasClass('is-adding-step'),
			isRendering: this.$el.hasClass('is-rendering'),
			isStepping: this.$el.hasClass('is-stepping'),
			isGoingToStep: this.$el.hasClass('is-going-to-step')
		}
	}

})

export default WizardProgress

WizardProgress.__filename__ = 'components/wizard-progress.js'
